const API_BASE = '/api';

// Get session token from URL or sessionStorage
function getSessionToken(): string | null {
  const params = new URLSearchParams(window.location.search);
  const idToken = params.get('id_token');
  if (idToken) {
    sessionStorage.setItem('shopify-id-token', idToken);
    return idToken;
  }
  return sessionStorage.getItem('shopify-id-token');
}

function getShopDomain(): string | null {
  const params = new URLSearchParams(window.location.search);
  const shop = params.get('shop');
  if (shop) {
    sessionStorage.setItem('shopify-shop', shop);
    return shop;
  }
  return sessionStorage.getItem('shopify-shop');
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: any;
  params?: Record<string, any>;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, params } = options;

  // Build URL with query params
  let url = `${API_BASE}${endpoint}`;
  const searchParams = new URLSearchParams();
  
  // Add shop param
  const shop = getShopDomain();
  if (shop) {
    searchParams.append('shop', shop);
  }
  
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });
  }
  
  const queryString = searchParams.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add session token if available
  const token = getSessionToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    method,
    headers,
    credentials: 'include',
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(url, config);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Network error' }));
    throw new Error(error.message || `Request failed with status ${response.status}`);
  }

  // Handle empty responses
  const text = await response.text();
  return (text ? JSON.parse(text) : {}) as T;
}

export const api = {
  get: <T>(endpoint: string, params?: Record<string, any>) =>
    request<T>(endpoint, { method: 'GET', params }),

  post: <T>(endpoint: string, body?: any) =>
    request<T>(endpoint, { method: 'POST', body }),

  put: <T>(endpoint: string, body?: any) =>
    request<T>(endpoint, { method: 'PUT', body }),

  delete: <T>(endpoint: string) =>
    request<T>(endpoint, { method: 'DELETE' }),
};
